import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function CareSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    let alive = true;
    if (!sessionId) { setState("error"); return; }
    const poll = async () => {
      if (!alive) return;
      try {
        const { data } = await api.get(`/care/status/${sessionId}`);
        if (data.status === "active") { setState("active"); track("Care Plan Active", { plan: data.plan }); return; }
        if (["canceled", "past_due"].includes(data.status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      attempts.current += 1;
      if (attempts.current > 8) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <AppShell>
      <div className="mx-auto max-w-lg px-8 py-20 text-center ltifade" data-testid="care-success">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-brand" size={34} />
            <p className="mt-5 font-heading text-xl font-semibold">Confirming your subscription…</p>
            <p className="mt-2 text-sm text-slate-500">This only takes a moment.</p>
          </>
        )}
        {state === "active" && (
          <>
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-50"><CheckCircle2 size={32} className="text-emerald-500" /></span>
            <p className="mt-5 font-heading text-2xl font-semibold">Your care plan is active</p>
            <p className="mt-2 text-slate-600">We'll keep your site healthy. You can manage or cancel your plan anytime from Build Studio.</p>
            <button data-testid="care-back-btn" onClick={() => navigate("/build")}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover">
              Back to Build Studio <ArrowRight size={16} />
            </button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto animate-spin text-brand" size={30} />
            <p className="mt-5 font-heading text-xl font-semibold">Still confirming…</p>
            <p className="mt-2 text-sm text-slate-500">Subscriptions can take a minute. Your plan activates automatically, refresh Build Studio shortly.</p>
            <button onClick={() => navigate("/build")} className="mt-6 rounded-lg border border-brand-line px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">Back to Build Studio</button>
          </>
        )}
        {state === "error" && (
          <>
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-red-50"><XCircle size={32} className="text-red-500" /></span>
            <p className="mt-5 font-heading text-xl font-semibold">We couldn't confirm that subscription</p>
            <p className="mt-2 text-slate-600">No charge is applied until it's confirmed. You can try again from Build Studio.</p>
            <button onClick={() => navigate("/build")} className="mt-6 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white hover:bg-brand-hover">Back to Build Studio</button>
          </>
        )}
      </div>
    </AppShell>
  );
}
